/**
 * Service Worker 注册（PWA 离线外壳）。
 *
 * 两个刻意的限制：
 *   1. **只在生产构建注册**。开发环境注册 SW 会缓存 Vite 开发产物，
 *      导致改代码后刷不出来（非常容易误判成"改动没生效"）；
 *      E2E 也跑在 preview/dev 上，注册 SW 会给测试引入不必要的缓存变量。
 *   2. SW 只缓存 `/assets/*` 与导航外壳，**绝不缓存 `/api/*`**，
 *      因此不会出现"看到过期章节内容"这类数据正确性问题。
 *      详见 public/sw.js。
 */

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 每小时检查一次更新

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  // 桌面客户端（VITE_DESKTOP=1）：离线外壳就是客户端里内嵌的那份产物本身，
  // SW 的缓存不但没有意义，还会在客户端升级后继续供上一版资源 —— 明确关掉。
  // 见 apps/desktop/scripts/build-web.mjs 与 docs/DESKTOP.md。
  if (import.meta.env.VITE_DESKTOP) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (registration) => {
        // 有新版本时提示刷新（不自动刷新，避免打断正在写作的用户）
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent('xiaotianshu:sw-update-ready'));
            }
          });
        });
        // 周期性检查更新（长时间开着的写作窗口也能拿到新版本）
        window.setInterval(() => {
          registration.update().catch(() => { /* 离线时忽略 */ });
        }, UPDATE_CHECK_INTERVAL_MS);
      },
      () => { /* 注册失败不影响主流程 */ },
    );
  });
}

/**
 * 监听在线/离线变化并派发事件，供 UI 提示。
 * 断网时最危险的是"以为保存成功了"，因此需要明确告知用户。
 */
export function watchConnectivity(): () => void {
  if (typeof window === 'undefined') return () => { /* noop */ };

  const notify = () => {
    window.dispatchEvent(new CustomEvent('xiaotianshu:connectivity', {
      detail: { online: navigator.onLine },
    }));
  };
  window.addEventListener('online', notify);
  window.addEventListener('offline', notify);
  // 首次同步一次（启动时就处于离线状态的情况）
  notify();

  return () => {
    window.removeEventListener('online', notify);
    window.removeEventListener('offline', notify);
  };
}

// ── 连接状态 → 提示决策（纯函数，便于单测） ──────────────────────

export interface ConnectivityToast {
  kind: 'success' | 'warn' | 'info';
  message: string;
  /** 0 表示常驻（不自动消失） */
  duration: number;
}

/**
 * 根据「上一次已知状态」与「本次状态」决定要提示什么。
 *
 * 拆成纯函数是因为决策里有几个容易写错的点：
 *   - 首次同步：在线时**不应**弹提示（启动就弹很吵），离线时才提示；
 *   - 状态未变化：不重复提示（online/offline 事件可能重复派发）；
 *   - 离线提示需要常驻，直到恢复在线，否则用户可能错过。
 *
 * @param previous 上一次已知状态；null 表示这是首次同步
 * @param online 本次状态
 * @returns 需要弹出的提示；null 表示无需提示
 */
export function connectivityToastFor(previous: boolean | null, online: boolean): ConnectivityToast | null {
  if (previous === null) {
    return online
      ? null
      : { kind: 'warn', message: '当前处于离线状态，改动可能无法保存到服务器', duration: 0 };
  }
  if (previous === online) return null;
  return online
    ? { kind: 'success', message: '网络已恢复', duration: 2600 }
    : { kind: 'warn', message: '网络已断开：改动可能无法保存，请检查连接后再继续', duration: 0 };
}
